import React from 'react';
import { useLayer, Arrow } from 'react-laag';
import { type TooltipType } from '.';
import { COLORS } from '../../constants/style/color';
import Heading from '../typography';
import { TooltipComponentContainer, TooltipComponentWrapper } from './tooltip.styled';

const ClickableTooltip = ({ children, tooltipText, size, variant }: TooltipType): JSX.Element => {
  const [isOpen, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const layerRef = React.useRef<HTMLDivElement>(null);

  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    isOpen,
    overflowContainer: true,
    auto: true,
    placement: 'top-center',
    triggerOffset: 12,
    containerOffset: 16,
    arrowOffset: 16,
    possiblePlacements: ['top-center', 'bottom-center'],
  });

  React.useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || layerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen]);

  return (
    <TooltipComponentContainer>
      <div
        className='tooltip-text'
        ref={(node) => {
          triggerRef.current = node;
          triggerProps.ref(node);
        }}
        onClick={() => {
          setOpen(!isOpen);
        }}>
        {children}
      </div>
      {renderLayer(
        <>
          {isOpen && (
            <TooltipComponentWrapper
              ref={(node: HTMLDivElement | null) => {
                layerRef.current = node;
                layerProps.ref(node);
              }}
              size={size ?? 'md'}
              variant={variant ?? 'light'}
              className='tooltip-box'
              style={layerProps.style}>
              <Heading variant='body-s' title={tooltipText} />
              <Arrow
                {...arrowProps}
                backgroundColor={variant === 'dark' ? COLORS.Black : COLORS.White}
                borderColor={variant === 'dark' ? COLORS.Black : COLORS.White}
                borderWidth={1}
                size={6}
              />
            </TooltipComponentWrapper>
          )}
        </>
      )}
    </TooltipComponentContainer>
  );
};

export default ClickableTooltip;
